import React from 'react';

import { useSearchService } from 'services';
import { SearchFormType } from 'global.types';
import {
  LectureSearch,
  LegislationSearch,
  PublicationSearch,
  StatisticsSearch
} from 'search-interfaces';

import './LibrarySearchInterface.scss';

/**
 * ILibrarySearchInterface
 *
 * @description Component properties for LibrarySearchInterface
 * @property {SearchThemeType} searchFormType     Search form type
 * @property {string}          sitecoreApiBaseUrl Base API URL for Sitecore API
 */
interface ILibrarySearchInterface {
  searchFormType: SearchFormType;
  sitecoreApiBaseUrl: string;
}

/**
 * LibrarySearchInterface
 *
 * @description Root component for the library search which handles 3 different search interfaces
 */
const LibrarySearchInterface: React.FC<ILibrarySearchInterface> = ({ searchFormType, sitecoreApiBaseUrl }) => {
  const searchService = useSearchService(sitecoreApiBaseUrl);

  /**
   * RenderCorrectSearchTheme
   *
   * @description Helper method to render the correct search based on the selected theme
   * @param {SearchFormType} _searchFormType Selected search form
   * @returns {React.ReactNode | null} Selected search component
   */
  const renderCorrectSearchTheme = (_searchFormType: SearchFormType): React.ReactNode | null => {
    switch (_searchFormType) {
      case 'publications':
        return <PublicationSearch searchService={searchService} />;
      case 'legislation':
        return <LegislationSearch searchService={searchService} />;
      case 'statistics':
        return <StatisticsSearch searchService={searchService} />;
      case 'lectures':
        return <LectureSearch searchService={searchService} />;
      default: {
        // eslint-disable-next-line no-console
        console.error('Selected theme can not be found please check the Sitecore parameter template and ensure the theme is one of the following: \'publications\', \'legislation\', \'statistics\'');
        return null;
      }
    }
  };

  return (
    <div className='library-publication-search'>
      {renderCorrectSearchTheme(searchFormType)}
    </div>
  );
};

export default LibrarySearchInterface;
